import React, { memo, useCallback, useMemo, useRef } from "react";

function OtpInputGroup({
  value,
  onChange,
  onComplete,
  themeColor,
  hasError,
}) {
  const inputRefs = useRef([]);

  const focusInput = useCallback((index) => {
    const nextInput = inputRefs.current[index];

    if (nextInput) {
      nextInput.focus();
    }
  }, []);

  const borderColor = useMemo(() => {
    if (hasError) {
      return "rgba(221, 67, 93, 1)";
    }

    return themeColor;
  }, [hasError, themeColor]);

  const handleDigitChange = (index, rawValue) => {
    const nextValue = String(rawValue || "").replace(/\D/g, "").slice(-1);
    const nextDigits = [...value];

    nextDigits[index] = nextValue;
    onChange(nextDigits);

    if (nextValue && index < nextDigits.length - 1) {
      focusInput(index + 1);
    }

    if (nextDigits.every((digit) => digit.length === 1)) {
      onComplete(nextDigits);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !value[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    const pastedDigits = (event.clipboardData || window.clipboardData)
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, value.length)
      .split("");

    if (pastedDigits.length === 0) {
      return;
    }

    const nextDigits = value.map((digit, index) => pastedDigits[index] || digit);
    onChange(nextDigits);

    if (nextDigits.every((digit) => digit.length === 1)) {
      onComplete(nextDigits);
      focusInput(nextDigits.length - 1);
      return;
    }

    focusInput(Math.min(pastedDigits.length, nextDigits.length - 1));
  };

  return (
    <div
      style={{
        marginTop: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {value.map((digit, index) => (
        <input
          key={`otp-digit-${index + 1}`}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          autoFocus={index === 0}
          type="text"
          inputMode="numeric"
          className="otp-input"
          value={digit}
          maxLength={1}
          name={`otp-${index + 1}`}
          style={{
            border: "none",
            outline: "none",
            borderBottom: `1px solid ${borderColor}`,
          }}
          onChange={(event) => handleDigitChange(index, event.target.value)}
          onPaste={index === 0 ? handlePaste : undefined}
          onKeyDown={(event) => handleKeyDown(index, event)}
        />
      ))}
    </div>
  );
}

export default memo(OtpInputGroup);
